import { AloftPrice, PairedCostResults } from '@aloft/toolkit-shared';
import { Input, Flex as WebFlex } from '@aloft/toolkit-sui';

import { chunk } from 'lodash';
import { useState } from 'react';

import { PDFOnly } from '../../../summary/components/PDFOnly.tsx';
import { Typography } from '../../../summary/components/Typography.tsx';
import { WebOnly } from '../../../summary/components/WebOnly.tsx';
import { SummarySection } from '../SummarySection.tsx';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { COMPS_PER_TABLE } from '../constants.js';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { AdjustmentBreakdownImprovementValueTable } from './AdjustmentBreakdownImprovementValueTable.tsx';

export const AdjustmentBreakdownImprovementValue = (props: Tkv2PairedCostSummaryInterfaceV2) => {
  const commentaryProps = props.exportableCommentaries.allImprovements;

  const compChunks = chunk(props.compSequence, COMPS_PER_TABLE);
  const [commentary, setCommentary] = useState(commentaryProps.commentary);

  if (!hasCostedImprovements(props.results)) {
    return null;
  }

  return (
    <SummarySection>
      <TitleAndExportToggleLockup
        {...props}
        title="Contributory Value of the Improvements & Related Adjustments"
        id="isMarketConditionsCommentaryInExportSwitch"
        commentary={commentaryProps}
      />
      <Typography variant="body1">
        The contributory value of each property's improvements was assessed using DwellingCost to
        estimate the Replacement Cost New (RCN). The quality levels specified in DwellingCost are
        represented as QL in the table below. All improvement adjustments are based on the
        difference between the cost to build the subject’s improvement and the cost to build the
        comparable’s improvement. The condition adjustment is based on the difference in the
        subject’s physical depreciation and the comparable’s physical depreciation.
      </Typography>
      <PDFOnly>
        <Typography variant="body1">{commentaryProps.commentary}</Typography>
      </PDFOnly>
      {compChunks.map((sequence, tableIdx) => (
        <AdjustmentBreakdownImprovementValueTable
          key={`market-conditions-table-${tableIdx}`}
          sequence={sequence}
          tableIdx={tableIdx}
          {...props}
        />
      ))}
      <WebOnly>
        <WebFlex marginTop="1rem" width="40rem">
          <Input
            type="multiline"
            onChange={setCommentary}
            value={commentary}
            onClear={() => setCommentary('')}
            onBlur={() => commentaryProps.updateCommentary(commentary)}
            resize="both"
            label="Commentary"
            size="sm"
          />
        </WebFlex>
      </WebOnly>
    </SummarySection>
  );
};
function hasCostedImprovements(results: PairedCostResults) {
  const subjCost = results.subject?.totals?.improvements.new.cost;
  const compCosts = Object.values(results.comps).map((comp) => comp.totals?.improvements.new.cost);
  return [subjCost, ...compCosts].some((cost) => cost?.gt(AloftPrice.zero));
}
